import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Joyride from "react-joyride";
import moment from "moment";

import { GetCreatedPapersThunk } from "../redux/thunk";
import { handleStage, handleJoyRideRunning, openPublishModal, closePublishModal, setSelectedExamId } from "../redux/slice";
import { FetchExamsBySchoolThunk, PublishQuestionPaperThunk, UnpublishQuestionPaperThunk } from "../redux/thunk";
import ErrorAlert from "./ErrorAlert";
import BootStrapTable from "../../components/table";
import { ProcessPapersData } from "./others";
import SkeletonLoader from "./loader/skeletonLoader";
import { getUserTourSteps } from "../../../../tour/QuestionPaperSteps"
import useSpeech from "../../../../hooks/useSpeech";

const headers = [
  { label: "S No", accessor: "sno" },
  { label: "Exam Name", accessor: "examName" },
  { label: "Subject Name", accessor: "subjectName" },
  { label: "Class Name", accessor: "className" },
  { label: "Max Marks", accessor: "maxMarks" },
  { label: "Created At", accessor: "created_at" },
  { label: "Actions", accessor: "actions" },
];

function QuestionPapers() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
 const isWalkthroughCompleted = localStorage.getItem("questionPaperTourCompleted") === "true";

  const { error, createdpapers, loader, isJoyRideRunning,
    publishModalOpen, selectedPaper, examsList, selectedExamId,
    loadingExams, publishing,
  } = useSelector((store) => store.createQstnPaper);

  const { user } = useSelector((store) => store.authSlice);

  const userTourSteps = useMemo(() => getUserTourSteps(), []);
  const [currentStep, setCurrentStep] = useState(0);
  const [showTour, setShowTour] = useState(false);

  const handleOpenPublishModal = (paper) => {
    dispatch(openPublishModal(paper));
    const schoolId =
      user?.school_id ||
      user?.admin_id ||
      user?.parent_id ||
      user?.id;
    if (schoolId) {
      dispatch(FetchExamsBySchoolThunk(schoolId));
    }
  };

  const handleConfirmPublish = (e) => {
    if (e) e.preventDefault();
    if (!selectedExamId) return;
    const userId = user?.id || user?.admin_id || user?.parent_id;
    dispatch(PublishQuestionPaperThunk({
      exam_id: selectedExamId,
      question_paper_id: selectedPaper.id,
      published_by: userId || null,
    }));
  };

  const handleUnpublishPaper = (paper) => {
    const unpublishId = paper.question_paper_id;
    if (window.confirm("Are you sure you want to unpublish this question paper from the exam?")) {
      dispatch(UnpublishQuestionPaperThunk(unpublishId));
    }
  };

  const processedPapers = ProcessPapersData(createdpapers, navigate, handleOpenPublishModal, handleUnpublishPaper);
  const { speak: speakText, isSpeaking, voicesReady, speechEnabled, setSpeechEnabled } = useSpeech();
  

  const closeButtonStyle = {
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 10001,
    backgroundColor: "#ff4757",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "44px",
    height: "44px",
    fontSize: "28px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(255, 71, 87, 0.4)",
    transition: "all 0.3s ease",
  };


  const CustomTooltip = ({
    index,
    step,
    primaryProps,
    closeProps,
    tooltipProps,
  }) => {
    return (
      <div
        {...tooltipProps}
        style={{
          background: "#fff",
          padding: "12px",
          borderRadius: "8px",
          maxWidth: "380px",
        }}
      >
        {/* Content */}
        <div style={{ marginBottom: "12px" }}>{step.content}</div>
  
        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          {/* Custom Skip */}
          {index > 0 && (
            <button
              onClick={(e) => {
      e.preventDefault();
      primaryProps.onClick(e); // ✅ pass event
    }}
              style={{
                background: "none",
                border: "none",
                color: "#6b7280",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              Skip
            </button>
          )}
  
          {/* Next / Finish Button */}
          {step.isFinalStep ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                primaryProps.onClick(e);
                setCurrentStep(0);
                dispatch(handleJoyRideRunning(false));
                window.location.href = "/dashboard";
              }}
              style={{
                background: "#6c4cff",
                color: "#fff",
                border: "none",
                padding: "6px 14px",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              Finish
            </button>
          ) : (
            step.showNext && (
              <button
                {...primaryProps}
                style={{
                  background: "#6c4cff",
                  color: "#fff",
                  border: "none",
                  padding: "6px 14px",
                  borderRadius: "6px",
                  cursor:  "pointer",
                  pointerEvents: "auto",
                }}
              >
                Next
              </button>
            )
          )}
  
        </div>
      </div>
    );
  };

  useEffect(() => {
    dispatch(GetCreatedPapersThunk({ type: 1 }));

    if (location.state?.resetData && user.admin_id !== 104) {
      dispatch(handleStage(1));

      // Wait for table DOM to render
      setTimeout(() => {
        const viewStepIndex = userTourSteps.findIndex(
          (step) => step.target === ".my-question-view-step"
        );

        if (viewStepIndex !== -1) {
          setCurrentStep(viewStepIndex);
          setShowTour(true);
        }
      }, 500);
    }
  }, [dispatch, location.state, user.admin_id, userTourSteps]);

  if (loader) return <SkeletonLoader type={1} />;

  return (
    <div>
      {error && <ErrorAlert message={error} />}

      {/* ✅ Tour Guide */}
      {/* {showTour && user.admin_id !== 104 && (
        <Joyride
              run={isJoyRideRunning && !isWalkthroughCompleted}
    tooltipComponent={CustomTooltip}
          steps={userTourSteps}
          stepIndex={currentStep}
           showSkipButton={true}
          continuous
          showProgress={false}
          spotlightClicks={true}
          disableOverlay={false}
          disableOverlayClose={true}
          hideCloseButton={true}
          styles={{
            options: { zIndex: 10000 },
            buttonBack: { display: "none" },
            overlay: { zIndex: 8000 },
            buttonNext: {
              backgroundColor: "#6c4cff",
              cursor: "pointer",
            },
            tooltip: {
              maxWidth: "380px",
              minHeight: "50px",
              padding: "5px",
              borderRadius: "8px",
            },
            tooltipContent: {
              padding: "6px 8px",
              fontSize: "16px",
              lineHeight: "1.4",
            },
          }}
          callback={(data) => {
            const { action, index, type, status,step } = data;

            if (type === "tooltip:render") return;

            if ((type === "tour:start" || type === "step:before") && step?.content) {
    requestAnimationFrame(() => {
      speakText(step.content);
    });
  }

            if (type === "step:after" && action === "next") {
              setCurrentStep(index + 1);
            }

            if (status === "finished" || status === "skipped") {
              setShowTour(false);
            }
          }}
        />
      )} */}

      <div className="container_bg px-2">
        <div className="py-3 px-5 mx-5">
          {processedPapers.length > 0 ? (
            <BootStrapTable headers={headers} data={processedPapers} />
          ) : (
            <h5>No data Found.</h5>
          )}
        </div>
      </div>

      {/* 🔹 Publish Question Paper Modal */}
      {publishModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Publish Question Paper to LMS Exam</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => dispatch(closePublishModal())}
                ></button>
              </div>
              <form onSubmit={handleConfirmPublish}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Select LMS Exam <span className="text-danger">*</span>
                    </label>
                    {loadingExams ? (
                      <div className="text-muted small py-2">Loading exams list...</div>
                    ) : (
                      <select
                        className="form-select"
                        value={selectedExamId}
                        onChange={(e) => dispatch(setSelectedExamId(e.target.value))}
                        required
                      >
                        <option value="">-- Select LMS Exam --</option>
                        {examsList.map((exam) => (
                          <option key={exam.id} value={exam.id}>
                            {exam.name} {exam.class_name ? `(Class: ${exam.class_name})` : ""} {exam.subject_name ? `- ${exam.subject_name}` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                    {!loadingExams && examsList.length === 0 && (
                      <div className="text-danger small mt-2">
                        No LMS exams found for your school. Please create an exam in LMS first.
                      </div>
                    )}
                  </div>

                  {/* Selected Exam Details */}
                  {selectedExamId && (() => {
                    const exam = examsList.find((e) => String(e.id) === String(selectedExamId));
                    if (!exam) return null;
                    return (
                      <div className="p-2 bg-light rounded border text-muted small">
                        <div className="fw-bold text-dark mb-1">Exam Details</div>
                        {exam.name && <div><strong>Exam Name:</strong> {exam.name}</div>}
                        {exam.class_name && <div><strong>Class:</strong> {exam.class_name}</div>}
                        {exam.subject_name && <div><strong>Subject:</strong> {exam.subject_name}</div>}
                        {exam.exam_type && <div><strong>Type:</strong> {exam.exam_type.replace(/_/g, " ")}</div>}
                        {exam.exam_date && <div><strong>Date:</strong> {moment(exam.exam_date).format("DD MMM YYYY")}</div>}
                        {exam.total_marks && <div><strong>Total Marks:</strong> {exam.total_marks}</div>}
                        {exam.duration_minutes && <div><strong>Duration:</strong> {exam.duration_minutes} mins</div>}
                      </div>
                    );
                  })()}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => dispatch(closePublishModal())}
                    disabled={publishing}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={publishing || !selectedExamId || loadingExams}
                  >
                    {publishing ? "Publishing..." : "Publish Paper"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 🔹 Joyride Close Button - Top Right Corner */}
      {/* {isJoyRideRunning && !isWalkthroughCompleted && (
        <button
          style={closeButtonStyle}
          onClick={() => {
            dispatch(handleJoyRideRunning(false));
            setCurrentStep(0);
          }}
          title="Close tour"
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#ff3838";
            e.target.style.transform = "scale(1.1)";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#ff4757";
            e.target.style.transform = "scale(1)";
          }}
        >
          ✕
        </button>
      )} */}
    </div>
  );
}

export default QuestionPapers;
